import type { CheckedNodes } from '@vuejs/components/shared/TreeView'
import humps from 'humps'

import type { ApiClient } from '../api'
import {
	getPackageFolderList,
	getPackageFolders,
	getPackages,
	putMovePackageAmongFolder,
} from '../apiRoutes'
import type {
	ContentPackageFolder,
	Package,
	PackageType,
} from './interfaces'

export async function createFolder(api: ApiClient, {
	name,
	parentContentPackageFolderId,
}: { name: string, parentContentPackageFolderId: number | null }) {
	const payload = {
		package_folder: {
			name,
			parent_content_package_folder_id: parentContentPackageFolderId,
		},
	}

	await api.post(getPackageFolders, payload)
}

export async function deleteContentPackageFolders(api: ApiClient, folderIds: number[]) {
	await api.delete(getPackageFolders, { ids: folderIds })
}

export async function getContentPackageFolderList(api: ApiClient) {
	const { data } = await api.get<ApiContentPackageFolderListResponse>(getPackageFolderList)

	return data
}

export async function getContentPackageFolders(api: ApiClient, {
	includeAll,
	parentFolderId,
}: { includeAll?: boolean, parentFolderId?: number }) {
	const payload = { include_all: includeAll, parent_folder_id: parentFolderId }

	const { data } = await api.get<ApiContentPackageFolderResponse>(getPackageFolders, payload)

	return data
}

export async function getContentPackages(api: ApiClient, options?: Options) {
	const { data } = await api.get<ApiContentPackagesResponse>(getPackages, { ...options })

	return data.map(c => ({
		assignedCourses: c.assigned_courses,
		certifications: c.certifications,
		copyPath: c.copy_path,
		cumulativeCourses: c.cumulative_courses,
		editPath: c.edit_path,
		hours: c.hours,
		id: c.id,
		isBuildingBlock: c.is_building_block,
		name: c.name,
		subPackages: c.sub_packages,
		type: c.type,
	}))
}

export async function getContentPackagesByStateRequirements(api: ApiClient, stateRequirementIds: number[]) {
	const { data } = await api
		.get<ApiStateRequirementsResponse>(`/training/packages/state_requirements?state_requirement_ids=${stateRequirementIds}`)

	return data.results.map(p => ({
		packageName: p.package_name,
		packages: humps.camelizeKeys(p.packages) as [Package],
	}))
}

export async function moveContentPackageOrFolder(api: ApiClient, { id, items }: { id: number | null, items: CheckedNodes[] }) {
	await api.put(putMovePackageAmongFolder, {
		destination_content_package_folder_id: id,
		items,
	})
}

export async function renameContentPackageFolder(api: ApiClient, { id, name }: { id: number, name: string }) {
	await api.put(`/content_package_folders/${id}`, { package_folder: { name } })
}

export type ApiContentPackageFolderListResponse = ContentPackageFolder[]

export type ApiContentPackageFolderResponse = ContentPackageFolder[]

export type ApiContentPackagesResponse = ApiContentPackage[]

export interface ApiStateRequirementsResponse { results: ApiPackages[] }

export interface ApiContentPackage {
	assigned_courses: number
	certifications: number
	copy_path: string
	cumulative_courses: number
	edit_path: string
	hours: string | null
	id: number
	is_building_block: boolean
	name: string
	sub_packages: string
	type: 'content_package'
}

export interface ApiContentPackageFolder {
	children?: ContentPackageFolder[]
	id: number
	level: number
	name: string
	parent_folder_id?: number | string | undefined
	type: PackageType
}

export interface ApiPackages {
	package_name: string
	packages: Package[]
}

export interface FolderPackageOptions {
	id: number | string | undefined
	name?: string
	type: PackageType | undefined
}

export interface Options {
	direction?: 'asc' | 'desc'
	format: 'json'
	is_building_block: boolean | undefined
	search?: string
	sort?: string
}

export interface Results {
	results: ApiPackages
}

export type PackageOptions = 'folder' | 'package'
